import type { Conversation, CrmContact, Inbox } from '../../api/types';
import { channelKeyForInbox, type ChannelKey } from '../../lib/commsMeta';

/**
 * Turning "reach this person" into something the module can open.
 *
 * The dispatch dashboard's call, mail and SMS icons sit on reservation rows,
 * job pages and finance tables. What they know about a customer is an address
 * — a phone number or an email — never a conversation id, so `?to=` is the
 * only thing they can send. Everything here answers the question that follows:
 * which conversation is that, and what do we do when there isn't one.
 *
 * Pure functions, no React and no API: the matching rules are where this can
 * go quietly wrong (a `+1` that one system stores and the other doesn't), so
 * they are the part that is tested directly.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A phone number reduced to digits — `+1 (212) 555-0100` → `12125550100`. */
const digits = (value: string): string => value.replace(/\D/g, '');

/** Fewer digits than this and `?to=` was never a phone number to begin with. */
const MIN_PHONE_DIGITS = 7;

/**
 * Two numbers are the same phone when their digits match, or when the only
 * thing between them is a country code.
 *
 * That one difference is the whole reason the rule exists: dispatch stores
 * `+12125550100` where a CRM contact may hold `2125550100`. Anything wider
 * starts matching a local number against a different area code's, and a wrong
 * match here opens a stranger's conversation — so it refuses rather than
 * guesses, and a seeded compose window is the cost of being wrong the safe way.
 */
const MIN_SHARED_DIGITS = 8;
const MAX_COUNTRY_CODE_DIGITS = 3;

export interface ReachIntent {
  /** The address exactly as the dashboard sent it. */
  to: string;
  isEmail: boolean;
  /** The tab the dashboard asked for, which is the channel it wants. */
  channelKey: ChannelKey;
}

/**
 * Read a `?to=` parameter, or null when there is nothing to act on.
 *
 * An address that is neither an email nor a plausible phone number is refused
 * rather than searched for: the contact list would match it as a NAME, and
 * opening a conversation because someone's name looked like the search box is
 * exactly the wrong answer.
 */
export function parseReachIntent(to: string | null, tab: string | null): ReachIntent | null {
  const trimmed = (to ?? '').trim();
  if (trimmed === '') return null;
  const isEmail = EMAIL_RE.test(trimmed);
  if (!isEmail && digits(trimmed).length < MIN_PHONE_DIGITS) return null;
  return {
    to: trimmed,
    isEmail,
    channelKey: (tab as ChannelKey | null) ?? 'all',
  };
}

/** Does this contact own the address the dashboard asked for? */
export function contactMatches(contact: CrmContact, intent: ReachIntent): boolean {
  if (intent.isEmail) {
    const email = contact.email?.trim().toLowerCase();
    return email !== undefined && email !== '' && email === intent.to.toLowerCase();
  }
  const want = digits(intent.to);
  const have = digits(contact.phoneNumber ?? '');
  if (have === '' || want === '') return false;
  if (have === want) return true;
  const shorter = have.length < want.length ? have : want;
  const longer = have.length < want.length ? want : have;
  return (
    shorter.length >= MIN_SHARED_DIGITS &&
    longer.length - shorter.length <= MAX_COUNTRY_CODE_DIGITS &&
    longer.endsWith(shorter)
  );
}

/**
 * The contact the address belongs to.
 *
 * `GET /contacts?q=` matches names and partial addresses too, so the row that
 * comes back first is not necessarily the person — the exact match is.
 *
 * One answer or none. Two contacts holding the same address — a shared office
 * line, a household, a duplicate an import left behind — is a question this
 * cannot answer, and taking the first of them opened a stranger's thread from
 * a dispatch link, with the call button then offering that thread's line. The
 * caller falls through to the composer seeded with the address, where the
 * agent picks.
 */
export function pickContact(contacts: CrmContact[], intent: ReachIntent): CrmContact | null {
  const matches = contacts.filter((contact) => contactMatches(contact, intent));
  return matches.length === 1 ? matches[0] : null;
}

const activityOf = (conversation: Conversation): number =>
  Date.parse(conversation.lastActivityAt ?? conversation.createdAt) || 0;

/**
 * Which of this contact's conversations to open, or null to compose instead.
 *
 * The channel is the agent's actual intent: pressing the MAIL icon on a
 * customer who has only ever been texted must open a new email, not their SMS
 * thread — the icon they pressed said which way they meant to reach out. The
 * one exception is a call, which the dashboard sends as `all`: any thread will
 * do, because what the agent wants from it is the Call button in its header.
 */
export function pickConversation(
  conversations: Conversation[],
  inboxes: Inbox[],
  channelKey: ChannelKey,
): Conversation | null {
  const byId = new Map(inboxes.map((inbox) => [inbox.id, inbox]));
  const candidates =
    channelKey === 'all'
      ? conversations
      : conversations.filter((conversation) => {
          const inbox = byId.get(conversation.inboxId);
          // An inbox this agent cannot see is not a conversation they can open.
          return inbox !== undefined && channelKeyForInbox(inbox) === channelKey;
        });
  if (candidates.length === 0) return null;
  return candidates.reduce((best, next) => (activityOf(next) > activityOf(best) ? next : best));
}
